import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  RouterProvider,
} from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError } from './api.ts';
import { dropDataOnAccessChange } from './auth.tsx';
import { reloadDataOnFocusChange } from './focus.ts';
import './i18n/index.ts';
import { AppLayout } from './layout.tsx';
import { AREAS, itemsOf, sectionPath } from './navigation.ts';
import { NewVersionNotice } from './new-version.tsx';
import { AuditPage } from './pages/Audit.tsx';
import { BranchesPage } from './pages/Branches.tsx';
import { CurrenciesPage } from './pages/Currencies.tsx';
import { DepartmentHomePage, SectionHomePage } from './pages/DepartmentHome.tsx';
import { DepartmentsPage } from './pages/Departments.tsx';
import { HomePage } from './pages/Home.tsx';
import { MyAccessPage } from './pages/MyAccess.tsx';
import { LegalEntitiesPage } from './pages/LegalEntities.tsx';
import { LoginPage } from './pages/Login.tsx';
import {
  ExpenseReimbursementsPage,
  FinancePendingPage,
  ReceivablesPage,
} from './pages/Finance.tsx';
import { TreasuryPage } from './pages/finance/Treasury.tsx';
import { ActualQueuePage } from './pages/controlling/ActualQueue.tsx';
import { ActualSourcesPage } from './pages/controlling/ActualSources.tsx';
import { PayablesPage } from './pages/finance/Payables.tsx';
import { ProvisionalReviewPage } from './pages/finance/ProvisionalReview.tsx';
import { BankAccountsPage } from './pages/finance/BankAccounts.tsx';
import { fpaMasterData } from './pages/fpa/Fpa.tsx';
import {
  FPA_CLASSIFICATION_REDIRECT,
  FPA_MASTER_DATA_REDIRECTS,
} from './pages/fpa/master-data-redirects.ts';
import { FpaCatalogPage, FpaPlLinesPage } from './pages/fpa/Catalog.tsx';
import { FpaPayrollPage } from './pages/fpa/Payroll.tsx';
import { RolesPage } from './pages/Roles.tsx';
import { AccessRequestsPage } from './pages/AccessRequests.tsx';
import { SingleHolderReleasePage } from './pages/SingleHolderRelease.tsx';
import { UsersPage } from './pages/Users.tsx';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});
// Troca de sessão ou de permissões: descarta o que estava carregado (sigilo de pessoal, v3.1).
dropDataOnAccessChange(queryClient);
reloadDataOnFocusChange(queryClient);

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <NewVersionNotice />
    </>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { error?: string; returnTo?: string } => ({
    error: typeof s.error === 'string' ? s.error : undefined,
    returnTo: typeof s.returnTo === 'string' ? s.returnTo : undefined,
  }),
  component: LoginPage,
});

// A Ajuda carrega à parte: o leitor de Markdown e os artigos só baixam quando alguém abre.
const helpPage = lazyRouteComponent(() => import('./pages/Help.tsx'), 'HelpPage');
const helpArticlePage = lazyRouteComponent(() => import('./pages/Help.tsx'), 'HelpArticlePage');

// Compras também carrega à parte: só baixa para quem abre as telas.
const purchaseNewPage = lazyRouteComponent(
  () => import('./pages/purchasing/NewRequest.tsx'),
  'NewPurchasePage',
);
const purchaseEditPage = lazyRouteComponent(
  () => import('./pages/purchasing/NewRequest.tsx'),
  'EditPurchasePage',
);
const purchaseMinePage = lazyRouteComponent(
  () => import('./pages/purchasing/Lists.tsx'),
  'MyPurchasesPage',
);
const purchaseTrackingPage = lazyRouteComponent(
  () => import('./pages/purchasing/Tracking.tsx'),
  'PurchaseTrackingPage',
);
const purchaseOrderPrintPage = lazyRouteComponent(
  () => import('./pages/purchasing/Order.tsx'),
  'PurchaseOrderPrintPage',
);
const purchaseTechnicalPage = lazyRouteComponent(
  () => import('./pages/purchasing/TechnicalView.tsx'),
  'TechnicalViewPage',
);
const purchaseDeliveriesPage = lazyRouteComponent(
  () => import('./pages/purchasing/Deliveries.tsx'),
  'DeliveriesPage',
);
const purchaseDeliveriesListPage = lazyRouteComponent(
  () => import('./pages/purchasing/Deliveries.tsx'),
  'DeliveriesListPage',
);
const purchasePendingPage = lazyRouteComponent(
  () => import('./pages/purchasing/HomeIndicators.tsx'),
  'PurchasePendingPage',
);
const purchaseQueuePage = lazyRouteComponent(
  () => import('./pages/purchasing/Lists.tsx'),
  'PurchaseQueuePage',
);
const classificationsPage = lazyRouteComponent(
  () => import('./pages/structure/Structure.tsx'),
  'ClassificationsPage',
);
const categoriesPage = lazyRouteComponent(
  () => import('./pages/structure/Structure.tsx'),
  'CategoriesPage',
);
const managementAccountsPage = lazyRouteComponent(
  () => import('./pages/structure/Accounts.tsx'),
  'ManagementAccountsPage',
);
const businessUnitsPage = lazyRouteComponent(
  () => import('./pages/structure/BusinessUnits.tsx'),
  'BusinessUnitsPage',
);
const subsidiariesPage = lazyRouteComponent(
  () => import('./pages/structure/Structure.tsx'),
  'SubsidiariesPage',
);
const importPendingPage = lazyRouteComponent(
  () => import('./pages/structure/Structure.tsx'),
  'ImportPendingPage',
);
const structureImportPage = lazyRouteComponent(
  () => import('./pages/structure/Structure.tsx'),
  'StructureImportPage',
);
const facilitiesPanelPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesRequests.tsx'),
  'FacilitiesPanelPage',
);
const facilitiesNewPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesRequests.tsx'),
  'FacilitiesNewPage',
);
const facilitiesStartPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesRequests.tsx'),
  'FacilitiesStartPage',
);
const facilitiesFormPreviewPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesRequests.tsx'),
  'FacilitiesFormPreviewPage',
);
const facilitiesRequestsPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesRequests.tsx'),
  'FacilitiesRequestsPage',
);
const facilitiesRequestPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesRequests.tsx'),
  'FacilitiesRequestPage',
);
const facilitiesQueuePage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesQueue.tsx'),
  'FacilitiesQueuePage',
);
const facilitiesPendingPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesPending.tsx'),
  'FacilitiesPendingPage',
);
const facilitiesCatalogPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesCatalog.tsx'),
  'FacilitiesCatalogPage',
);
const facilitiesServicePage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesCatalog.tsx'),
  'FacilitiesServicePage',
);
// Facilities, manutenção (ADR 0036): ordens de serviço, checklists e prestadores.
const workOrdersPage = lazyRouteComponent(
  () => import('./pages/facilities/Maintenance.tsx'),
  'WorkOrdersPage',
);
const workOrderNewPage = lazyRouteComponent(
  () => import('./pages/facilities/Maintenance.tsx'),
  'WorkOrderNewPage',
);
const workOrderPage = lazyRouteComponent(
  () => import('./pages/facilities/Maintenance.tsx'),
  'WorkOrderPage',
);
// Parâmetros de Facilities e indicadores da manutenção (ADR 0036, 3c-1).
const facilitiesSettingsPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesSettings.tsx'),
  'FacilitiesSettingsPage',
);
const maintenanceIndicatorsPage = lazyRouteComponent(
  () => import('./pages/facilities/FacilitiesSettings.tsx'),
  'MaintenanceIndicatorsPage',
);
// Manutenção preventiva (ADR 0036, 3b): planos, ficha do plano e agenda.
const maintenancePlansPage = lazyRouteComponent(
  () => import('./pages/facilities/MaintenancePlans.tsx'),
  'MaintenancePlansPage',
);
const maintenancePlanPage = lazyRouteComponent(
  () => import('./pages/facilities/MaintenancePlans.tsx'),
  'MaintenancePlanPage',
);
const preventiveCalendarPage = lazyRouteComponent(
  () => import('./pages/facilities/MaintenancePlans.tsx'),
  'PreventiveCalendarPage',
);
// PMOC (ADR 0036, 3c-3): lista e ficha.
const pmocListPage = lazyRouteComponent(
  () => import('./pages/facilities/Pmoc.tsx'),
  'PmocListPage',
);
const pmocPage = lazyRouteComponent(() => import('./pages/facilities/Pmoc.tsx'), 'PmocPage');
// Materiais de consumo e EPI (ADR 0027, Entrega 4): lista e ficha do item.
const materialsPage = lazyRouteComponent(
  () => import('./pages/facilities/Materials.tsx'),
  'MaterialsPage',
);
// Serviços administrativos (ADR 0027, Entrega 6): garagem e chaves.
const travelPage = lazyRouteComponent(() => import('./pages/facilities/Travel.tsx'), 'TravelPage');
const spacesPage = lazyRouteComponent(() => import('./pages/facilities/Spaces.tsx'), 'SpacesPage');
const facilitiesReportsPage = lazyRouteComponent(
  () => import('./pages/facilities/Reports.tsx'),
  'FacilitiesReportsPage',
);
const travelPolicyPage = lazyRouteComponent(
  () => import('./pages/facilities/TravelPolicy.tsx'),
  'TravelPolicyPage',
);
const travelPolicyVersionPage = lazyRouteComponent(
  () => import('./pages/facilities/TravelPolicy.tsx'),
  'TravelPolicyVersionPage',
);
const servicesPage = lazyRouteComponent(
  () => import('./pages/facilities/Services.tsx'),
  'ServicesPage',
);
const materialItemPage = lazyRouteComponent(
  () => import('./pages/facilities/Materials.tsx'),
  'MaterialItemPage',
);
const checklistsPage = lazyRouteComponent(
  () => import('./pages/facilities/Maintenance.tsx'),
  'ChecklistsPage',
);
const checklistPage = lazyRouteComponent(
  () => import('./pages/facilities/Maintenance.tsx'),
  'ChecklistPage',
);
const providersPage = lazyRouteComponent(
  () => import('./pages/facilities/Maintenance.tsx'),
  'ProvidersPage',
);
const providerPage = lazyRouteComponent(
  () => import('./pages/facilities/Maintenance.tsx'),
  'ProviderPage',
);
const locationsPage = lazyRouteComponent(
  () => import('./pages/facilities/Locations.tsx'),
  'LocationsPage',
);
// TI: contratações de software, licenças e catálogo.
const itDashboardPage = lazyRouteComponent(
  () => import('./pages/it/ItDashboard.tsx'),
  'ItDashboardPage',
);
const itSubscriptionsPage = lazyRouteComponent(
  () => import('./pages/it/ItSubscriptions.tsx'),
  'ItSubscriptionsPage',
);
const itSubscriptionNewPage = lazyRouteComponent(
  () => import('./pages/it/ItSubscriptions.tsx'),
  'ItSubscriptionNewPage',
);
const itSubscriptionEditPage = lazyRouteComponent(
  () => import('./pages/it/ItSubscriptions.tsx'),
  'ItSubscriptionEditPage',
);
const itSubscriptionPage = lazyRouteComponent(
  () => import('./pages/it/ItSubscriptionDetail.tsx'),
  'ItSubscriptionPage',
);
const itCatalogPage = lazyRouteComponent(() => import('./pages/it/ItCatalog.tsx'), 'ItCatalogPage');
const itSoftwarePage = lazyRouteComponent(
  () => import('./pages/it/ItCatalog.tsx'),
  'ItSoftwarePage',
);
// TI, Fase 2: equipamentos (visão técnica sobre o patrimônio), kit por pessoa e devoluções.
const itDevicesPage = lazyRouteComponent(() => import('./pages/it/ItDevices.tsx'), 'ItDevicesPage');
const itDevicesDashboardPage = lazyRouteComponent(
  () => import('./pages/it/ItDevices.tsx'),
  'ItDevicesDashboardPage',
);
const itDevicePage = lazyRouteComponent(
  () => import('./pages/it/ItDeviceDetail.tsx'),
  'ItDevicePage',
);
const itDeviceTermSheetPage = lazyRouteComponent(
  () => import('./pages/it/ItDeviceDetail.tsx'),
  'ItDeviceTermSheetPage',
);
const itDeviceImportPage = lazyRouteComponent(
  () => import('./pages/it/ItDeviceImport.tsx'),
  'ItDeviceImportPage',
);
const itMySoftwarePage = lazyRouteComponent(
  () => import('./pages/it/ItMySoftware.tsx'),
  'ItMySoftwarePage',
);
const itLicenseImportPage = lazyRouteComponent(
  () => import('./pages/it/ItLicenseImport.tsx'),
  'ItLicenseImportPage',
);
const itTicketNewPage = lazyRouteComponent(
  () => import('./pages/it/ItTicketNew.tsx'),
  'ItTicketNewPage',
);
const itMyTicketsPage = lazyRouteComponent(
  () => import('./pages/it/ItTicketLists.tsx'),
  'ItMyTicketsPage',
);
const itServiceDeskPage = lazyRouteComponent(
  () => import('./pages/it/ItTicketLists.tsx'),
  'ItServiceDeskPage',
);
const itTicketPage = lazyRouteComponent(
  () => import('./pages/it/ItTicketDetail.tsx'),
  'ItTicketPage',
);
const itTicketCatalogPage = lazyRouteComponent(
  () => import('./pages/it/ItTicketCatalog.tsx'),
  'ItTicketCatalogPage',
);
const itTicketServicePage = lazyRouteComponent(
  () => import('./pages/it/ItTicketCatalog.tsx'),
  'ItTicketServicePage',
);
const itKnowledgePage = lazyRouteComponent(
  () => import('./pages/it/ItKnowledge.tsx'),
  'ItKnowledgePage',
);
const itKbArticlePage = lazyRouteComponent(
  () => import('./pages/it/ItKnowledge.tsx'),
  'ItKbArticlePage',
);
const itProblemsPage = lazyRouteComponent(
  () => import('./pages/it/ItProblems.tsx'),
  'ItProblemsPage',
);
const itProblemPage = lazyRouteComponent(
  () => import('./pages/it/ItProblems.tsx'),
  'ItProblemPage',
);
const itRoutinesPage = lazyRouteComponent(
  () => import('./pages/it/ItRoutines.tsx'),
  'ItRoutinesPage',
);
const itRoutinePage = lazyRouteComponent(
  () => import('./pages/it/ItRoutines.tsx'),
  'ItRoutinePage',
);
const itPendingPage = lazyRouteComponent(() => import('./pages/it/ItPending.tsx'), 'ItPendingPage');
const itDeskDashboardPage = lazyRouteComponent(
  () => import('./pages/it/ItDeskDashboard.tsx'),
  'ItDeskDashboardPage',
);
const itRepairMovementsPage = lazyRouteComponent(
  () => import('./pages/it/ItRepairMovements.tsx'),
  'ItRepairMovementsPage',
);
const masterdataPendingPage = lazyRouteComponent(
  () => import('./pages/masterdata/Pending.tsx'),
  'MasterdataPendingPage',
);
const accountingAccountsPage = lazyRouteComponent(
  () => import('./pages/masterdata/AccountingAccounts.tsx'),
  'AccountingAccountsPage',
);
const importsPage = lazyRouteComponent(
  () => import('./pages/masterdata/Imports.tsx'),
  'ImportsPage',
);
const importHistoryPage = lazyRouteComponent(
  () => import('./pages/masterdata/Imports.tsx'),
  'ImportHistoryPage',
);
const importBatchPage = lazyRouteComponent(
  () => import('./pages/masterdata/Imports.tsx'),
  'ImportBatchPage',
);
const itPeoplePage = lazyRouteComponent(() => import('./pages/it/ItPeople.tsx'), 'ItPeoplePage');
const itPersonPage = lazyRouteComponent(() => import('./pages/it/ItPeople.tsx'), 'ItPersonPage');
const itOffboardingsPage = lazyRouteComponent(
  () => import('./pages/it/ItPeople.tsx'),
  'ItOffboardingsPage',
);
const itOffboardingPage = lazyRouteComponent(
  () => import('./pages/it/ItPeople.tsx'),
  'ItOffboardingPage',
);
const itServicesPage = lazyRouteComponent(
  () => import('./pages/it/ItFinance.tsx'),
  'ItServicesPage',
);
const itServicePage = lazyRouteComponent(() => import('./pages/it/ItFinance.tsx'), 'ItServicePage');
const itInvoicesPage = lazyRouteComponent(
  () => import('./pages/it/ItFinance.tsx'),
  'ItInvoicesPage',
);
const itInvoicePage = lazyRouteComponent(() => import('./pages/it/ItFinance.tsx'), 'ItInvoicePage');
const itSystemsPage = lazyRouteComponent(() => import('./pages/it/ItAccess.tsx'), 'ItSystemsPage');
const itAccessPage = lazyRouteComponent(() => import('./pages/it/ItAccess.tsx'), 'ItAccessPage');
const itAccessReviewsPage = lazyRouteComponent(
  () => import('./pages/it/ItAccessReviews.tsx'),
  'ItAccessReviewsPage',
);
const itAccessReviewPage = lazyRouteComponent(
  () => import('./pages/it/ItAccessReviews.tsx'),
  'ItAccessReviewPage',
);
const itMyAccessPage = lazyRouteComponent(
  () => import('./pages/it/ItAccessReviews.tsx'),
  'ItMyAccessPage',
);
const itPoliciesPage = lazyRouteComponent(
  () => import('./pages/it/ItAccessReviews.tsx'),
  'ItPoliciesPage',
);
const itSoftwareReviewsPage = lazyRouteComponent(
  () => import('./pages/it/ItAccessReviews.tsx'),
  'ItSoftwareReviewsPage',
);
const itGovernanceDashboardPage = lazyRouteComponent(
  () => import('./pages/it/ItGovernance.tsx'),
  'ItGovernanceDashboardPage',
);
const itControlsPage = lazyRouteComponent(
  () => import('./pages/it/ItGovernance.tsx'),
  'ItControlsPage',
);
const itControlExceptionsPage = lazyRouteComponent(
  () => import('./pages/it/ItGovernance.tsx'),
  'ItControlExceptionsPage',
);
const itRaciPage = lazyRouteComponent(() => import('./pages/it/ItGovernance.tsx'), 'ItRaciPage');
const itIncidentsPage = lazyRouteComponent(
  () => import('./pages/it/ItGovernance.tsx'),
  'ItIncidentsPage',
);
const itEmergencyVaultPage = lazyRouteComponent(
  () => import('./pages/it/ItGovernance.tsx'),
  'ItEmergencyVaultPage',
);
const itStockPage = lazyRouteComponent(() => import('./pages/it/ItMaintenance.tsx'), 'ItStockPage');
const itPreventivePage = lazyRouteComponent(
  () => import('./pages/it/ItMaintenance.tsx'),
  'ItPreventivePage',
);
const itProjectsPage = lazyRouteComponent(
  () => import('./pages/it/ItMaintenance.tsx'),
  'ItProjectsPage',
);
const itChangesPage = lazyRouteComponent(
  () => import('./pages/it/ItMaintenance.tsx'),
  'ItChangesPage',
);
const itAccessDashboardPage = lazyRouteComponent(
  () => import('./pages/it/ItAccess.tsx'),
  'ItAccessDashboardPage',
);
const itCostsPage = lazyRouteComponent(() => import('./pages/it/ItCosts.tsx'), 'ItCostsPage');
const itFinanceDashboardPage = lazyRouteComponent(
  () => import('./pages/it/ItCosts.tsx'),
  'ItFinanceDashboardPage',
);
const itOnboardingsPage = lazyRouteComponent(
  () => import('./pages/it/ItOnboarding.tsx'),
  'ItOnboardingsPage',
);
const itOnboardingPage = lazyRouteComponent(
  () => import('./pages/it/ItOnboarding.tsx'),
  'ItOnboardingPage',
);
const assetPanelPage = lazyRouteComponent(
  () => import('./pages/assets/AssetList.tsx'),
  'AssetPanelPage',
);
const assetListPage = lazyRouteComponent(
  () => import('./pages/assets/AssetList.tsx'),
  'AssetListPage',
);
const myAssetsPage = lazyRouteComponent(
  () => import('./pages/assets/AssetList.tsx'),
  'MyAssetsPage',
);
const assetNewPage = lazyRouteComponent(
  () => import('./pages/assets/AssetList.tsx'),
  'AssetNewPage',
);
const assetPage = lazyRouteComponent(() => import('./pages/assets/AssetDetail.tsx'), 'AssetPage');
const assetLabelsPage = lazyRouteComponent(
  () => import('./pages/assets/AssetLabels.tsx'),
  'AssetLabelsPage',
);
const assetTagPage = lazyRouteComponent(
  () => import('./pages/assets/AssetLabels.tsx'),
  'AssetTagPage',
);
const assetClassesPage = lazyRouteComponent(
  () => import('./pages/assets/AssetAdmin.tsx'),
  'AssetClassesPage',
);
const assetPolicyPage = lazyRouteComponent(
  () => import('./pages/assets/AssetAdmin.tsx'),
  'AssetPolicyPage',
);
const assetImportPage = lazyRouteComponent(
  () => import('./pages/assets/AssetAdmin.tsx'),
  'AssetImportPage',
);
const assetTermsPage = lazyRouteComponent(
  () => import('./pages/assets/AssetAdmin.tsx'),
  'AssetTermsPage',
);
const assetInventoriesPage = lazyRouteComponent(
  () => import('./pages/assets/AssetInventory.tsx'),
  'AssetInventoriesPage',
);
const assetInventoryPage = lazyRouteComponent(
  () => import('./pages/assets/AssetInventory.tsx'),
  'AssetInventoryPage',
);
const legalPanelPage = lazyRouteComponent(
  () => import('./pages/legal/LegalRequests.tsx'),
  'LegalPanelPage',
);
const legalNewPage = lazyRouteComponent(
  () => import('./pages/legal/LegalRequests.tsx'),
  'LegalNewPage',
);
const legalStartPage = lazyRouteComponent(
  () => import('./pages/legal/LegalRequests.tsx'),
  'LegalStartPage',
);
const legalFormPreviewPage = lazyRouteComponent(
  () => import('./pages/legal/LegalRequests.tsx'),
  'LegalFormPreviewPage',
);
const legalRequestsPage = lazyRouteComponent(
  () => import('./pages/legal/LegalRequests.tsx'),
  'LegalRequestsPage',
);
const legalRequestPage = lazyRouteComponent(
  () => import('./pages/legal/LegalRequests.tsx'),
  'LegalRequestPage',
);
const representativesPage = lazyRouteComponent(
  () => import('./pages/Representatives.tsx'),
  'RepresentativesPage',
);
const representativePage = lazyRouteComponent(
  () => import('./pages/Representatives.tsx'),
  'RepresentativePage',
);
const legalTemplatesPage = lazyRouteComponent(
  () => import('./pages/legal/LegalTemplates.tsx'),
  'LegalTemplatesPage',
);
const legalClausesPage = lazyRouteComponent(
  () => import('./pages/legal/LegalTemplates.tsx'),
  'LegalClausesPage',
);
const legalModelsPage = lazyRouteComponent(
  () => import('./pages/legal/LegalTemplates.tsx'),
  'LegalModelsPage',
);
const legalTemplatePage = lazyRouteComponent(
  () => import('./pages/legal/LegalTemplates.tsx'),
  'LegalTemplatePage',
);
const legalMyContractsPage = lazyRouteComponent(
  () => import('./pages/legal/LegalMyContracts.tsx'),
  'LegalMyContractsPage',
);
const legalContractsPage = lazyRouteComponent(
  () => import('./pages/legal/LegalContracts.tsx'),
  'LegalContractsPage',
);
const legalContractNewPage = lazyRouteComponent(
  () => import('./pages/legal/LegalContracts.tsx'),
  'LegalContractNewPage',
);
const legalContractPage = lazyRouteComponent(
  () => import('./pages/legal/LegalContracts.tsx'),
  'LegalContractPage',
);
const legalContractReconciliationPage = lazyRouteComponent(
  () => import('./pages/legal/LegalContractReconciliation.tsx'),
  'LegalContractReconciliationPage',
);
const legalContractImportPage = lazyRouteComponent(
  () => import('./pages/legal/LegalContracts.tsx'),
  'LegalContractImportPage',
);
const legalIpWorksPage = lazyRouteComponent(
  () => import('./pages/legal/LegalIpWorks.tsx'),
  'LegalIpWorksPage',
);
const legalIpWorkPage = lazyRouteComponent(
  () => import('./pages/legal/LegalIpWorks.tsx'),
  'LegalIpWorkPage',
);
const legalIpLicensesPage = lazyRouteComponent(
  () => import('./pages/legal/LegalIpLicenses.tsx'),
  'LegalIpLicensesPage',
);
const legalIpLicensePage = lazyRouteComponent(
  () => import('./pages/legal/LegalIpLicenses.tsx'),
  'LegalIpLicensePage',
);
const legalPiracyListPage = lazyRouteComponent(
  () => import('./pages/legal/LegalPiracy.tsx'),
  'LegalPiracyListPage',
);
const legalPiracyCasePage = lazyRouteComponent(
  () => import('./pages/legal/LegalPiracy.tsx'),
  'LegalPiracyCasePage',
);
const legalLawsuitsPage = lazyRouteComponent(
  () => import('./pages/legal/LegalLawsuits.tsx'),
  'LegalLawsuitsPage',
);
const legalLawsuitPage = lazyRouteComponent(
  () => import('./pages/legal/LegalLawsuits.tsx'),
  'LegalLawsuitPage',
);
const legalExternalPage = lazyRouteComponent(
  () => import('./pages/legal/LegalExternal.tsx'),
  'LegalExternalPage',
);
const legalExternalNewPage = lazyRouteComponent(
  () => import('./pages/legal/LegalExternal.tsx'),
  'LegalExternalNewPage',
);
const legalExternalMatterPage = lazyRouteComponent(
  () => import('./pages/legal/LegalExternal.tsx'),
  'LegalExternalMatterPage',
);
const legalIpPage = lazyRouteComponent(() => import('./pages/legal/LegalIp.tsx'), 'LegalIpPage');
const legalIpNewPage = lazyRouteComponent(
  () => import('./pages/legal/LegalIp.tsx'),
  'LegalIpNewPage',
);
const legalIpAssetPage = lazyRouteComponent(
  () => import('./pages/legal/LegalIp.tsx'),
  'LegalIpAssetPage',
);
const legalIpWorkImportPage = lazyRouteComponent(
  () => import('./pages/legal/LegalIpWorkImport.tsx'),
  'LegalIpWorkImportPage',
);
const legalIpImportPage = lazyRouteComponent(
  () => import('./pages/legal/LegalIp.tsx'),
  'LegalIpImportPage',
);
const legalAgendaPage = lazyRouteComponent(
  () => import('./pages/legal/LegalAgenda.tsx'),
  'LegalAgendaPage',
);
const legalAgendaNewPage = lazyRouteComponent(
  () => import('./pages/legal/LegalAgenda.tsx'),
  'LegalAgendaNewPage',
);
const legalAgendaActionPage = lazyRouteComponent(
  () => import('./pages/legal/LegalAgenda.tsx'),
  'LegalAgendaActionPage',
);
const legalAlertsPage = lazyRouteComponent(
  () => import('./pages/legal/LegalAlerts.tsx'),
  'LegalAlertsPage',
);
const legalReportsPage = lazyRouteComponent(
  () => import('./pages/legal/LegalReports.tsx'),
  'LegalReportsPage',
);
const legalPendingPage = lazyRouteComponent(
  () => import('./pages/legal/LegalPending.tsx'),
  'LegalPendingPage',
);
const legalQueuePage = lazyRouteComponent(
  () => import('./pages/legal/LegalHandling.tsx'),
  'LegalQueuePage',
);
const legalCatalogPage = lazyRouteComponent(
  () => import('./pages/legal/LegalCatalog.tsx'),
  'LegalCatalogPage',
);
const legalServicePage = lazyRouteComponent(
  () => import('./pages/legal/LegalCatalog.tsx'),
  'LegalServicePage',
);
const costCentersPage = lazyRouteComponent(
  () => import('./pages/governance/CostCenters.tsx'),
  'CostCentersPage',
);
const appointmentsPage = lazyRouteComponent(
  () => import('./pages/governance/Appointments.tsx'),
  'AppointmentsPage',
);
const approvalDeadlinesPage = lazyRouteComponent(
  () => import('./pages/governance/Deadlines.tsx'),
  'ApprovalDeadlinesPage',
);
const slaProcessesPage = lazyRouteComponent(
  () => import('./pages/sla/Processes.tsx'),
  'SlaProcessesPage',
);
const slaCalendarsPage = lazyRouteComponent(
  () => import('./pages/sla/Calendars.tsx'),
  'SlaCalendarsPage',
);
const slaDashboardPage = lazyRouteComponent(
  () => import('./pages/sla/Dashboard.tsx'),
  'SlaDashboardPage',
);
const slaPoliciesPage = lazyRouteComponent(
  () => import('./pages/sla/Policies.tsx'),
  'SlaPoliciesPage',
);
const absencesPage = lazyRouteComponent(
  () => import('./pages/governance/Absences.tsx'),
  'AbsencesPage',
);
const approvalLevelsPage = lazyRouteComponent(
  () => import('./pages/governance/Approvers.tsx'),
  'ApprovalLevelsPage',
);
const approvalResponsiblesPage = lazyRouteComponent(
  () => import('./pages/governance/Approvers.tsx'),
  'ApprovalResponsiblesPage',
);
const approvalMatrixPage = lazyRouteComponent(
  () => import('./pages/governance/Matrix.tsx'),
  'ApprovalMatrixPage',
);
const approvalsPage = lazyRouteComponent(() => import('./pages/Approvals.tsx'), 'ApprovalsPage');
const feedbackListPage = lazyRouteComponent(
  () => import('./pages/Feedback.tsx'),
  'FeedbackListPage',
);
const feedbackDetailPage = lazyRouteComponent(
  () => import('./pages/Feedback.tsx'),
  'FeedbackDetailPage',
);
const notificationsPage = lazyRouteComponent(
  () => import('./pages/Notifications.tsx'),
  'NotificationsPage',
);
const purchaseQuoteRulesPage = lazyRouteComponent(
  () => import('./pages/purchasing/QuoteRules.tsx'),
  'QuoteRulesPage',
);
const purchaseDetailPage = lazyRouteComponent(
  () => import('./pages/purchasing/Detail.tsx'),
  'PurchaseDetailPage',
);

// Parceiros (fornecedores) também carrega à parte.
const partnersPage = lazyRouteComponent(() => import('./pages/partners/Lists.tsx'), 'PartnersPage');
const portalAccessPage = lazyRouteComponent(
  () => import('./pages/partners/Portal.tsx'),
  'PortalAccessPage',
);
const partnerNewPage = lazyRouteComponent(
  () => import('./pages/partners/Lists.tsx'),
  'NewPartnerPage',
);
const partnerPendingDocumentsPage = lazyRouteComponent(
  () => import('./pages/partners/Documents.tsx'),
  'PendingDocumentsPage',
);
const partnerDocumentTypesPage = lazyRouteComponent(
  () => import('./pages/partners/Documents.tsx'),
  'DocumentTypesPage',
);
// Central de Conhecimento (ADR 0018).
const knowledgeLibraryPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeLibraryPage',
);
const knowledgeDocumentPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeDocumentPage',
);
const knowledgeReviewPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeReviewPage',
);
const knowledgeVersionPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeVersionPage',
);
const knowledgeManualsPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeManualsPage',
);
const knowledgePopsPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgePopsPage',
);
const knowledgeItosPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeItosPage',
);
const knowledgeNewsPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeNewsPage',
);
const knowledgeFaqPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeFaqPage',
);
const knowledgeIssuesPage = lazyRouteComponent(
  () => import('./pages/knowledge/Knowledge.tsx'),
  'KnowledgeIssuesPage',
);
const partnerDetailPage = lazyRouteComponent(
  () => import('./pages/partners/Detail.tsx'),
  'PartnerDetailPage',
);

const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppLayout });
const parent = { getParentRoute: () => appRoute };

// FP&A, Fase 1B: calendário, cenários, premissas, acessos e o motor de planejamento.
const fpaCalendarPage = lazyRouteComponent(
  () => import('./pages/fpa/Settings.tsx'),
  'FpaCalendarPage',
);
const fpaScenariosPage = lazyRouteComponent(
  () => import('./pages/fpa/Settings.tsx'),
  'FpaScenariosPage',
);
const fpaAssumptionsPage = lazyRouteComponent(
  () => import('./pages/fpa/Settings.tsx'),
  'FpaAssumptionsPage',
);
const fpaAccessPage = lazyRouteComponent(() => import('./pages/fpa/Settings.tsx'), 'FpaAccessPage');
// Cadastros corporativos no FP&A: só consulta, mantidos em Sistema (ADR 0032, seção 1).
// O invólucro é criado depois que a tela carrega: uma rota preguiçosa dentro de outro componente
// quebra o carregamento (erro do React sobre use()).
const fpaAccountsPage = lazyRouteComponent(() =>
  import('./pages/structure/Accounts.tsx').then((m) => ({
    default: fpaMasterData(m.ManagementAccountsPage, '/structure/accounts'),
  })),
);
const fpaPlReportPage = lazyRouteComponent(() => import('./pages/fpa/Pl.tsx'), 'FpaPlReportPage');
const fpaPlDashboardPage = lazyRouteComponent(
  () => import('./pages/fpa/PlDashboard.tsx'),
  'FpaPlDashboardPage',
);
const fpaExceptionsPage = lazyRouteComponent(
  () => import('./pages/fpa/Review.tsx'),
  'FpaExceptionsPage',
);
const fpaPlanningPage = lazyRouteComponent(
  () => import('./pages/fpa/Planning.tsx'),
  'FpaPlanningPage',
);
const fpaBudgetPage = lazyRouteComponent(() => import('./pages/fpa/Planning.tsx'), 'FpaBudgetPage');
const fpaForecastPage = lazyRouteComponent(
  () => import('./pages/fpa/Planning.tsx'),
  'FpaForecastPage',
);
const fpaReferencePage = lazyRouteComponent(
  () => import('./pages/fpa/Planning.tsx'),
  'FpaReferencePage',
);
const fpaComparePage = lazyRouteComponent(
  () => import('./pages/fpa/Analysis.tsx'),
  'FpaComparePage',
);
const fpaAcquisitionPage = lazyRouteComponent(
  () => import('./pages/fpa/Acquisition.tsx'),
  'FpaAcquisitionPage',
);
const fpaDashboardPage = lazyRouteComponent(
  () => import('./pages/fpa/Analysis.tsx'),
  'FpaDashboardPage',
);
const fpaActualsPage = lazyRouteComponent(
  () => import('./pages/fpa/Actuals.tsx'),
  'FpaActualsPage',
);
const fpaVersionPage = lazyRouteComponent(
  () => import('./pages/fpa/Planning.tsx'),
  'FpaVersionPage',
);

// Uma página inicial por departamento, tirada do menu: departamento novo não precisa de rota à mão.
// O FP&A tem página inicial própria (PR-8: Meu planejamento).
const fpaHomePage = lazyRouteComponent(() => import('./pages/fpa/FpaHome.tsx'), 'FpaHomePage');
const fpaPendingPage = lazyRouteComponent(
  () => import('./pages/fpa/FpaHome.tsx'),
  'FpaPendingPage',
);
const departmentRoutes = AREAS.flatMap(itemsOf)
  .filter((item) => item.overview && item.to !== '/fpa')
  .map((item) => createRoute({ ...parent, path: item.to, component: DepartmentHomePage }));
// Uma página por seção do menu enxuto (Dados Mestres): /<departamento>/<seção>.
const sectionRoutes = AREAS.flatMap((area) =>
  (area.sections ?? []).map((section) =>
    createRoute({ ...parent, path: sectionPath(area, section), component: SectionHomePage }),
  ),
);
// A Administração da Plataforma virou a seção Plataforma dos Dados Mestres (19/10/2026).
const platformRedirect = createRoute({
  ...parent,
  path: '/platform',
  beforeLoad: () => {
    throw redirect({ to: '/system/platform' });
  },
});
// Compras › Meus prazos: atalho para a aba "Meus prazos" do painel do SLA corporativo.
const purchasingDeadlinesRedirect = createRoute({
  ...parent,
  path: '/purchasing/deadlines',
  beforeLoad: () => {
    throw redirect({ href: '/governance/sla/dashboard?tab=mine', replace: true });
  },
});
// As contas bancárias das empresas ficam só em Dados Mestres (ADR 0073, revisão de 30/10/2026).
const financeBankAccountsRedirect = createRoute({
  ...parent,
  path: '/finance/bank-accounts',
  beforeLoad: () => {
    throw redirect({ to: '/masterdata/bank-accounts', replace: true });
  },
});

// Dados Mestres: Pessoas, Colaboradores e Cargos (ADR 0039).
const employeesPage = lazyRouteComponent(
  () => import('./pages/people/Employees.tsx'),
  'EmployeesPage',
);
const employeePage = lazyRouteComponent(
  () => import('./pages/people/EmployeeDetail.tsx'),
  'EmployeeDetailPage',
);
const personsPage = lazyRouteComponent(() => import('./pages/people/Persons.tsx'), 'PersonsPage');
const personPage = lazyRouteComponent(
  () => import('./pages/people/Persons.tsx'),
  'PersonDetailPage',
);
const employmentTypesPage = lazyRouteComponent(
  () => import('./pages/people/Persons.tsx'),
  'EmploymentTypesPage',
);
const intranetPostsPage = lazyRouteComponent(
  () => import('./pages/intranet/IntranetPosts.tsx'),
  'IntranetPostsPage',
);
const intranetLinksPage = lazyRouteComponent(
  () => import('./pages/intranet/IntranetLinks.tsx'),
  'IntranetLinksPage',
);
const orgChartPage = lazyRouteComponent(
  () => import('./pages/people/OrgChart.tsx'),
  'OrgChartPage',
);
const birthdaysPage = lazyRouteComponent(
  () => import('./pages/people/Birthdays.tsx'),
  'BirthdaysPage',
);
const mailingPage = lazyRouteComponent(() => import('./pages/people/Mailing.tsx'), 'MailingPage');
const myProfilePage = lazyRouteComponent(
  () => import('./pages/people/MyProfile.tsx'),
  'MyProfilePage',
);
const positionsPage = lazyRouteComponent(
  () => import('./pages/people/Positions.tsx'),
  'PositionsPage',
);
const acknowledgementsPage = lazyRouteComponent(
  () => import('./pages/people/Acknowledgements.tsx'),
  'AcknowledgementsPage',
);
const payrollCostPage = lazyRouteComponent(
  () => import('./pages/people/PayrollCost.tsx'),
  'PayrollCostPage',
);
const accessGrantPage = lazyRouteComponent(
  () => import('./pages/people/AccessGrant.tsx'),
  'AccessGrantPage',
);
const correctionsPage = lazyRouteComponent(
  () => import('./pages/people/Corrections.tsx'),
  'CorrectionsPage',
);
const preferencesPage = lazyRouteComponent(
  () => import('./pages/people/Preferences.tsx'),
  'PreferencesPage',
);
const movementsPage = lazyRouteComponent(
  () => import('./pages/people/Movements.tsx'),
  'MovementsPage',
);
const allocationsPage = lazyRouteComponent(
  () => import('./pages/people/Movements.tsx'),
  'AllocationsPage',
);
const jobsPage = lazyRouteComponent(() => import('./pages/career/Jobs.tsx'), 'JobsPage');
const jobFamiliesPage = lazyRouteComponent(
  () => import('./pages/career/Catalog.tsx'),
  'JobFamiliesPage',
);
const jobLevelsPage = lazyRouteComponent(
  () => import('./pages/career/Catalog.tsx'),
  'JobLevelsPage',
);
const careerTracksPage = lazyRouteComponent(
  () => import('./pages/career/Catalog.tsx'),
  'CareerTracksPage',
);
const salaryGradesPage = lazyRouteComponent(
  () => import('./pages/career/SalaryGrades.tsx'),
  'SalaryGradesPage',
);
const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    createRoute({ ...parent, path: '/', component: HomePage }),
    createRoute({ ...parent, path: '/my-access', component: MyAccessPage }),
    ...departmentRoutes,
    ...sectionRoutes,
    platformRedirect,
    purchasingDeadlinesRedirect,
    financeBankAccountsRedirect,
    createRoute({ ...parent, path: '/legal-entities', component: LegalEntitiesPage }),
    createRoute({ ...parent, path: '/branches', component: BranchesPage }),
    createRoute({ ...parent, path: '/departments', component: DepartmentsPage }),
    createRoute({ ...parent, path: '/currencies', component: CurrenciesPage }),
    createRoute({ ...parent, path: '/representatives', component: representativesPage }),
    createRoute({ ...parent, path: '/representatives/$id', component: representativePage }),
    createRoute({ ...parent, path: '/people/employees', component: employeesPage }),
    createRoute({ ...parent, path: '/people/employees/$id', component: employeePage }),
    createRoute({ ...parent, path: '/people/persons', component: personsPage }),
    createRoute({ ...parent, path: '/people/persons/$id', component: personPage }),
    createRoute({ ...parent, path: '/people/org-chart', component: orgChartPage }),
    createRoute({ ...parent, path: '/hr/intranet', component: intranetPostsPage }),
    createRoute({ ...parent, path: '/hr/intranet/links', component: intranetLinksPage }),
    createRoute({ ...parent, path: '/people/birthdays', component: birthdaysPage }),
    createRoute({ ...parent, path: '/people/mailing', component: mailingPage }),
    createRoute({ ...parent, path: '/people/preferences', component: preferencesPage }),
    createRoute({ ...parent, path: '/me', component: myProfilePage }),
    createRoute({ ...parent, path: '/people/corrections', component: correctionsPage }),
    createRoute({ ...parent, path: '/people/access', component: accessGrantPage }),
    createRoute({ ...parent, path: '/people/payroll-cost', component: payrollCostPage }),
    createRoute({
      ...parent,
      path: '/people/acknowledgements',
      component: acknowledgementsPage,
    }),
    createRoute({ ...parent, path: '/people/positions', component: positionsPage }),
    createRoute({ ...parent, path: '/people/movements', component: movementsPage }),
    createRoute({ ...parent, path: '/people/allocations', component: allocationsPage }),
    createRoute({ ...parent, path: '/people/employment-types', component: employmentTypesPage }),
    createRoute({ ...parent, path: '/career/jobs', component: jobsPage }),
    createRoute({ ...parent, path: '/career/families', component: jobFamiliesPage }),
    createRoute({ ...parent, path: '/career/levels', component: jobLevelsPage }),
    createRoute({ ...parent, path: '/career/tracks', component: careerTracksPage }),
    createRoute({ ...parent, path: '/career/grades', component: salaryGradesPage }),
    createRoute({ ...parent, path: '/users', component: UsersPage }),
    createRoute({ ...parent, path: '/roles', component: RolesPage }),
    createRoute({ ...parent, path: '/access-requests', component: AccessRequestsPage }),
    createRoute({ ...parent, path: '/single-holder', component: SingleHolderReleasePage }),
    createRoute({ ...parent, path: '/audit', component: AuditPage }),
    createRoute({ ...parent, path: '/finance/pending', component: FinancePendingPage }),
    createRoute({ ...parent, path: '/controlling/actual-queue', component: ActualQueuePage }),
    createRoute({ ...parent, path: '/controlling/actual-sources', component: ActualSourcesPage }),
    createRoute({ ...parent, path: '/finance/payables', component: PayablesPage }),
    createRoute({
      ...parent,
      path: '/finance/provisional-reviews/$id',
      component: ProvisionalReviewPage,
    }),
    createRoute({ ...parent, path: '/finance/receivables', component: ReceivablesPage }),
    createRoute({ ...parent, path: '/finance/treasury', component: TreasuryPage }),
    createRoute({ ...parent, path: '/masterdata/bank-accounts', component: BankAccountsPage }),
    createRoute({
      ...parent,
      path: '/finance/expense-reimbursements',
      component: ExpenseReimbursementsPage,
    }),
    createRoute({ ...parent, path: '/notifications', component: notificationsPage }),
    createRoute({ ...parent, path: '/feedback', component: feedbackListPage }),
    createRoute({ ...parent, path: '/feedback/$id', component: feedbackDetailPage }),
    createRoute({ ...parent, path: '/governance/cost-centers', component: costCentersPage }),
    createRoute({ ...parent, path: '/structure/categories', component: categoriesPage }),
    createRoute({ ...parent, path: '/structure/subsidiaries', component: subsidiariesPage }),
    createRoute({ ...parent, path: '/structure/import-pending', component: importPendingPage }),
    createRoute({ ...parent, path: '/structure/import', component: structureImportPage }),
    createRoute({ ...parent, path: '/facilities/panel', component: facilitiesPanelPage }),
    createRoute({ ...parent, path: '/facilities/new', component: facilitiesNewPage }),
    createRoute({ ...parent, path: '/facilities/new/$serviceId', component: facilitiesStartPage }),
    createRoute({
      ...parent,
      path: '/facilities/preview/$serviceId',
      component: facilitiesFormPreviewPage,
    }),
    createRoute({ ...parent, path: '/facilities/requests', component: facilitiesRequestsPage }),
    createRoute({ ...parent, path: '/facilities/requests/$id', component: facilitiesRequestPage }),
    createRoute({ ...parent, path: '/facilities/queue', component: facilitiesQueuePage }),
    createRoute({ ...parent, path: '/facilities/pending', component: facilitiesPendingPage }),
    createRoute({ ...parent, path: '/facilities/catalog', component: facilitiesCatalogPage }),
    createRoute({ ...parent, path: '/facilities/catalog/$id', component: facilitiesServicePage }),
    createRoute({ ...parent, path: '/facilities/locations', component: locationsPage }),
    createRoute({ ...parent, path: '/facilities/work-orders', component: workOrdersPage }),
    createRoute({ ...parent, path: '/facilities/work-orders/new', component: workOrderNewPage }),
    createRoute({ ...parent, path: '/facilities/work-orders/$id', component: workOrderPage }),
    createRoute({
      ...parent,
      path: '/facilities/maintenance-plans',
      component: maintenancePlansPage,
    }),
    createRoute({
      ...parent,
      path: '/facilities/maintenance-plans/$id',
      component: maintenancePlanPage,
    }),
    createRoute({ ...parent, path: '/facilities/settings', component: facilitiesSettingsPage }),
    createRoute({
      ...parent,
      path: '/facilities/maintenance-indicators',
      component: maintenanceIndicatorsPage,
    }),
    createRoute({
      ...parent,
      path: '/facilities/preventive-calendar',
      component: preventiveCalendarPage,
    }),
    createRoute({ ...parent, path: '/facilities/pmocs', component: pmocListPage }),
    createRoute({ ...parent, path: '/facilities/pmocs/$id', component: pmocPage }),
    createRoute({ ...parent, path: '/facilities/materials', component: materialsPage }),
    createRoute({ ...parent, path: '/facilities/materials/$id', component: materialItemPage }),
    createRoute({ ...parent, path: '/facilities/services', component: servicesPage }),
    createRoute({ ...parent, path: '/facilities/travel', component: travelPage }),
    createRoute({ ...parent, path: '/facilities/spaces', component: spacesPage }),
    createRoute({ ...parent, path: '/facilities/reports', component: facilitiesReportsPage }),
    createRoute({ ...parent, path: '/facilities/travel-policy', component: travelPolicyPage }),
    createRoute({
      ...parent,
      path: '/facilities/travel-policy/$id',
      component: travelPolicyVersionPage,
    }),
    createRoute({ ...parent, path: '/facilities/checklists', component: checklistsPage }),
    createRoute({ ...parent, path: '/facilities/checklists/$id', component: checklistPage }),
    createRoute({ ...parent, path: '/facilities/providers', component: providersPage }),
    createRoute({ ...parent, path: '/facilities/providers/$partnerId', component: providerPage }),
    createRoute({ ...parent, path: '/it/pending', component: itPendingPage }),
    createRoute({ ...parent, path: '/it/dashboard', component: itDashboardPage }),
    createRoute({ ...parent, path: '/it/my-software', component: itMySoftwarePage }),
    createRoute({ ...parent, path: '/it/subscriptions', component: itSubscriptionsPage }),
    createRoute({ ...parent, path: '/it/subscriptions/new', component: itSubscriptionNewPage }),
    createRoute({ ...parent, path: '/it/subscriptions/$id', component: itSubscriptionPage }),
    createRoute({
      ...parent,
      path: '/it/subscriptions/$id/edit',
      component: itSubscriptionEditPage,
    }),
    createRoute({ ...parent, path: '/it/catalog', component: itCatalogPage }),
    createRoute({ ...parent, path: '/it/catalog/software/$id', component: itSoftwarePage }),
    createRoute({ ...parent, path: '/it/devices', component: itDevicesPage }),
    createRoute({ ...parent, path: '/it/devices/dashboard', component: itDevicesDashboardPage }),
    createRoute({ ...parent, path: '/it/devices/import', component: itDeviceImportPage }),
    createRoute({ ...parent, path: '/it/subscriptions/import', component: itLicenseImportPage }),
    createRoute({ ...parent, path: '/it/repair-movements', component: itRepairMovementsPage }),
    createRoute({ ...parent, path: '/it/tickets/new', component: itTicketNewPage }),
    createRoute({ ...parent, path: '/it/tickets/mine', component: itMyTicketsPage }),
    createRoute({ ...parent, path: '/it/tickets/$id', component: itTicketPage }),
    createRoute({ ...parent, path: '/it/service-desk', component: itServiceDeskPage }),
    createRoute({ ...parent, path: '/it/service-desk/dashboard', component: itDeskDashboardPage }),
    createRoute({ ...parent, path: '/it/knowledge', component: itKnowledgePage }),
    createRoute({ ...parent, path: '/it/knowledge/$id', component: itKbArticlePage }),
    createRoute({ ...parent, path: '/it/problems', component: itProblemsPage }),
    createRoute({ ...parent, path: '/it/problems/$id', component: itProblemPage }),
    createRoute({ ...parent, path: '/it/routines', component: itRoutinesPage }),
    createRoute({ ...parent, path: '/it/routines/$id', component: itRoutinePage }),
    createRoute({ ...parent, path: '/it/service-desk/catalog', component: itTicketCatalogPage }),
    createRoute({
      ...parent,
      path: '/it/service-desk/catalog/$id',
      component: itTicketServicePage,
    }),
    createRoute({ ...parent, path: '/masterdata/pending', component: masterdataPendingPage }),
    createRoute({ ...parent, path: '/masterdata/imports', component: importsPage }),
    createRoute({
      ...parent,
      path: '/masterdata/accounting-accounts',
      component: accountingAccountsPage,
    }),
    createRoute({ ...parent, path: '/masterdata/imports/history', component: importHistoryPage }),
    createRoute({ ...parent, path: '/masterdata/imports/batches/$id', component: importBatchPage }),
    createRoute({ ...parent, path: '/it/devices/$id', component: itDevicePage }),
    createRoute({ ...parent, path: '/it/devices/$id/term', component: itDeviceTermSheetPage }),
    createRoute({ ...parent, path: '/it/people', component: itPeoplePage }),
    createRoute({ ...parent, path: '/it/people/$userId', component: itPersonPage }),
    createRoute({ ...parent, path: '/it/offboardings', component: itOffboardingsPage }),
    createRoute({ ...parent, path: '/it/offboardings/$id', component: itOffboardingPage }),
    createRoute({ ...parent, path: '/it/onboardings', component: itOnboardingsPage }),
    createRoute({ ...parent, path: '/it/costs', component: itCostsPage }),
    createRoute({ ...parent, path: '/it/finance', component: itFinanceDashboardPage }),
    createRoute({ ...parent, path: '/it/services', component: itServicesPage }),
    createRoute({ ...parent, path: '/it/services/$id', component: itServicePage }),
    createRoute({ ...parent, path: '/it/invoices', component: itInvoicesPage }),
    createRoute({ ...parent, path: '/it/invoices/$id', component: itInvoicePage }),
    createRoute({ ...parent, path: '/it/systems', component: itSystemsPage }),
    createRoute({ ...parent, path: '/it/access', component: itAccessPage }),
    createRoute({ ...parent, path: '/it/access/dashboard', component: itAccessDashboardPage }),
    createRoute({ ...parent, path: '/it/access-reviews', component: itAccessReviewsPage }),
    createRoute({ ...parent, path: '/it/access-reviews/$id', component: itAccessReviewPage }),
    createRoute({ ...parent, path: '/it/my-access', component: itMyAccessPage }),
    createRoute({ ...parent, path: '/it/stock', component: itStockPage }),
    createRoute({ ...parent, path: '/it/preventive', component: itPreventivePage }),
    createRoute({ ...parent, path: '/it/projects', component: itProjectsPage }),
    createRoute({ ...parent, path: '/it/changes', component: itChangesPage }),
    createRoute({ ...parent, path: '/it/governance', component: itGovernanceDashboardPage }),
    createRoute({ ...parent, path: '/it/controls', component: itControlsPage }),
    createRoute({ ...parent, path: '/it/control-exceptions', component: itControlExceptionsPage }),
    createRoute({ ...parent, path: '/it/raci', component: itRaciPage }),
    createRoute({ ...parent, path: '/it/incidents', component: itIncidentsPage }),
    createRoute({ ...parent, path: '/it/emergency-vault', component: itEmergencyVaultPage }),
    createRoute({ ...parent, path: '/it/policies', component: itPoliciesPage }),
    createRoute({ ...parent, path: '/it/software-reviews', component: itSoftwareReviewsPage }),
    createRoute({ ...parent, path: '/it/onboardings/$id', component: itOnboardingPage }),
    createRoute({ ...parent, path: '/assets/panel', component: assetPanelPage }),
    createRoute({ ...parent, path: '/assets/list', component: assetListPage }),
    createRoute({ ...parent, path: '/assets/mine', component: myAssetsPage }),
    createRoute({ ...parent, path: '/assets/new', component: assetNewPage }),
    createRoute({ ...parent, path: '/assets/terms', component: assetTermsPage }),
    createRoute({ ...parent, path: '/assets/inventories', component: assetInventoriesPage }),
    createRoute({ ...parent, path: '/assets/inventories/$id', component: assetInventoryPage }),
    createRoute({ ...parent, path: '/assets/import', component: assetImportPage }),
    createRoute({ ...parent, path: '/assets/classes', component: assetClassesPage }),
    createRoute({ ...parent, path: '/assets/policy', component: assetPolicyPage }),
    createRoute({
      ...parent,
      path: '/assets/labels',
      component: assetLabelsPage,
      validateSearch: (s: Record<string, unknown>) => ({
        ids: typeof s.ids === 'string' ? s.ids : '',
      }),
    }),
    createRoute({ ...parent, path: '/assets/t/$tag', component: assetTagPage }),
    createRoute({ ...parent, path: '/assets/$id', component: assetPage }),
    createRoute({ ...parent, path: '/legal/panel', component: legalPanelPage }),
    createRoute({ ...parent, path: '/legal/new', component: legalNewPage }),
    createRoute({ ...parent, path: '/legal/new/$serviceId', component: legalStartPage }),
    createRoute({ ...parent, path: '/legal/preview/$serviceId', component: legalFormPreviewPage }),
    createRoute({ ...parent, path: '/legal/requests', component: legalRequestsPage }),
    createRoute({ ...parent, path: '/legal/queue', component: legalQueuePage }),
    createRoute({ ...parent, path: '/legal/pending', component: legalPendingPage }),
    createRoute({ ...parent, path: '/legal/library/templates', component: legalTemplatesPage }),
    createRoute({ ...parent, path: '/legal/library/templates/$id', component: legalTemplatePage }),
    createRoute({ ...parent, path: '/legal/library/clauses', component: legalClausesPage }),
    createRoute({ ...parent, path: '/legal/library/models', component: legalModelsPage }),
    createRoute({ ...parent, path: '/legal/library/clauses/$id', component: legalTemplatePage }),
    createRoute({ ...parent, path: '/legal/library/models/$id', component: legalTemplatePage }),
    createRoute({ ...parent, path: '/legal/contracts', component: legalContractsPage }),
    createRoute({ ...parent, path: '/legal/my-contracts', component: legalMyContractsPage }),
    createRoute({ ...parent, path: '/legal/contracts/new', component: legalContractNewPage }),
    createRoute({ ...parent, path: '/legal/contracts/import', component: legalContractImportPage }),
    createRoute({
      ...parent,
      path: '/legal/contracts/reconciliation',
      component: legalContractReconciliationPage,
    }),
    createRoute({ ...parent, path: '/legal/contracts/$id', component: legalContractPage }),
    createRoute({ ...parent, path: '/legal/lawsuits', component: legalLawsuitsPage }),
    createRoute({ ...parent, path: '/legal/lawsuits/$id', component: legalLawsuitPage }),
    createRoute({ ...parent, path: '/legal/external', component: legalExternalPage }),
    createRoute({ ...parent, path: '/legal/external/new', component: legalExternalNewPage }),
    createRoute({ ...parent, path: '/legal/external/$id', component: legalExternalMatterPage }),
    createRoute({ ...parent, path: '/legal/ip', component: legalIpPage }),
    createRoute({ ...parent, path: '/legal/ip/new', component: legalIpNewPage }),
    createRoute({ ...parent, path: '/legal/ip/import', component: legalIpImportPage }),
    createRoute({ ...parent, path: '/legal/ip/$id', component: legalIpAssetPage }),
    createRoute({ ...parent, path: '/legal/ip-works', component: legalIpWorksPage }),
    createRoute({ ...parent, path: '/legal/ip-works/import', component: legalIpWorkImportPage }),
    createRoute({ ...parent, path: '/legal/ip-works/$id', component: legalIpWorkPage }),
    createRoute({ ...parent, path: '/legal/ip-licenses', component: legalIpLicensesPage }),
    createRoute({ ...parent, path: '/legal/ip-licenses/$id', component: legalIpLicensePage }),
    createRoute({ ...parent, path: '/legal/piracy', component: legalPiracyListPage }),
    createRoute({ ...parent, path: '/legal/piracy/$id', component: legalPiracyCasePage }),
    createRoute({ ...parent, path: '/legal/agenda', component: legalAgendaPage }),
    createRoute({ ...parent, path: '/legal/alerts', component: legalAlertsPage }),
    createRoute({ ...parent, path: '/legal/reports', component: legalReportsPage }),
    createRoute({ ...parent, path: '/legal/agenda/new', component: legalAgendaNewPage }),
    createRoute({
      ...parent,
      path: '/legal/agenda/actions/$id',
      component: legalAgendaActionPage,
    }),
    createRoute({ ...parent, path: '/legal/requests/$id', component: legalRequestPage }),
    createRoute({ ...parent, path: '/legal/catalog', component: legalCatalogPage }),
    createRoute({ ...parent, path: '/legal/catalog/$id', component: legalServicePage }),
    createRoute({
      ...parent,
      path: '/structure/classifications/$category',
      component: classificationsPage,
    }),
    createRoute({ ...parent, path: '/structure/accounts', component: managementAccountsPage }),
    createRoute({ ...parent, path: '/structure/business-units', component: businessUnitsPage }),
    createRoute({ ...parent, path: '/fpa/settings/accounts', component: fpaAccountsPage }),
    // Consultas antigas de cadastros corporativos no FP&A: só levam à tela oficial (decisão M1).
    ...FPA_MASTER_DATA_REDIRECTS.map(({ from, to }) =>
      createRoute({
        ...parent,
        path: from,
        beforeLoad: () => {
          throw redirect({ to, replace: true });
        },
      }),
    ),
    createRoute({
      ...parent,
      path: FPA_CLASSIFICATION_REDIRECT.from,
      beforeLoad: ({ params }) => {
        throw redirect({ to: FPA_CLASSIFICATION_REDIRECT.to, params, replace: true });
      },
    }),
    createRoute({ ...parent, path: '/fpa/settings/catalog', component: FpaCatalogPage }),
    createRoute({ ...parent, path: '/fpa/exceptions', component: fpaExceptionsPage }),
    createRoute({ ...parent, path: '/fpa/pl-report', component: fpaPlReportPage }),
    createRoute({ ...parent, path: '/fpa/pl-dashboard', component: fpaPlDashboardPage }),
    createRoute({ ...parent, path: '/fpa/settings/pl', component: FpaPlLinesPage }),
    createRoute({ ...parent, path: '/fpa/settings/payroll', component: FpaPayrollPage }),
    createRoute({ ...parent, path: '/fpa/settings/calendar', component: fpaCalendarPage }),
    createRoute({ ...parent, path: '/fpa/settings/assumptions', component: fpaAssumptionsPage }),
    createRoute({ ...parent, path: '/fpa/settings/access', component: fpaAccessPage }),
    createRoute({ ...parent, path: '/fpa/scenarios', component: fpaScenariosPage }),
    createRoute({ ...parent, path: '/fpa', component: fpaHomePage }),
    createRoute({ ...parent, path: '/fpa/pending', component: fpaPendingPage }),
    createRoute({ ...parent, path: '/fpa/planning', component: fpaPlanningPage }),
    createRoute({ ...parent, path: '/fpa/budget', component: fpaBudgetPage }),
    createRoute({ ...parent, path: '/fpa/forecast', component: fpaForecastPage }),
    createRoute({ ...parent, path: '/fpa/reference', component: fpaReferencePage }),
    createRoute({ ...parent, path: '/fpa/versions/$id', component: fpaVersionPage }),
    createRoute({ ...parent, path: '/fpa/actuals', component: fpaActualsPage }),
    createRoute({ ...parent, path: '/fpa/compare', component: fpaComparePage }),
    createRoute({ ...parent, path: '/fpa/acquisition', component: fpaAcquisitionPage }),
    createRoute({ ...parent, path: '/fpa/dashboard', component: fpaDashboardPage }),
    createRoute({ ...parent, path: '/governance/levels', component: approvalLevelsPage }),
    createRoute({ ...parent, path: '/governance/absences', component: absencesPage }),
    createRoute({ ...parent, path: '/governance/sla/processes', component: slaProcessesPage }),
    createRoute({ ...parent, path: '/governance/sla/calendars', component: slaCalendarsPage }),
    createRoute({ ...parent, path: '/governance/sla/policies', component: slaPoliciesPage }),
    createRoute({ ...parent, path: '/governance/sla/dashboard', component: slaDashboardPage }),
    createRoute({ ...parent, path: '/governance/deadlines', component: approvalDeadlinesPage }),
    createRoute({ ...parent, path: '/governance/appointments', component: appointmentsPage }),
    createRoute({ ...parent, path: '/governance/matrix', component: approvalMatrixPage }),
    createRoute({ ...parent, path: '/approvals', component: approvalsPage }),
    createRoute({
      ...parent,
      path: '/governance/responsibles',
      component: approvalResponsiblesPage,
    }),
    createRoute({ ...parent, path: '/help', component: helpPage }),
    createRoute({ ...parent, path: '/help/$slug', component: helpArticlePage }),
    createRoute({ ...parent, path: '/purchasing/new', component: purchaseNewPage }),
    createRoute({ ...parent, path: '/purchasing/requests', component: purchaseMinePage }),
    createRoute({ ...parent, path: '/purchasing/requests/$id', component: purchaseDetailPage }),
    createRoute({ ...parent, path: '/purchasing/requests/$id/edit', component: purchaseEditPage }),
    createRoute({
      ...parent,
      path: '/purchasing/requests/$id/technical',
      component: purchaseTechnicalPage,
    }),
    createRoute({
      ...parent,
      path: '/purchasing/requests/$id/deliveries',
      component: purchaseDeliveriesPage,
    }),
    createRoute({
      ...parent,
      path: '/purchasing/deliveries',
      component: purchaseDeliveriesListPage,
    }),
    createRoute({
      ...parent,
      path: '/purchasing/requests/$id/order/$orderId/print',
      component: purchaseOrderPrintPage,
    }),
    createRoute({ ...parent, path: '/purchasing/queue', component: purchaseQueuePage }),
    createRoute({ ...parent, path: '/purchasing/pending', component: purchasePendingPage }),
    createRoute({ ...parent, path: '/purchasing/tracking', component: purchaseTrackingPage }),
    createRoute({ ...parent, path: '/purchasing/quote-rules', component: purchaseQuoteRulesPage }),
    createRoute({ ...parent, path: '/partners', component: partnersPage }),
    createRoute({ ...parent, path: '/partners/new', component: partnerNewPage }),
    createRoute({ ...parent, path: '/knowledge', component: knowledgeLibraryPage }),
    createRoute({ ...parent, path: '/knowledge/manuals', component: knowledgeManualsPage }),
    createRoute({ ...parent, path: '/knowledge/pops', component: knowledgePopsPage }),
    createRoute({ ...parent, path: '/knowledge/itos', component: knowledgeItosPage }),
    createRoute({ ...parent, path: '/knowledge/news', component: knowledgeNewsPage }),
    createRoute({ ...parent, path: '/knowledge/faq', component: knowledgeFaqPage }),
    createRoute({ ...parent, path: '/knowledge/review', component: knowledgeReviewPage }),
    createRoute({ ...parent, path: '/knowledge/issues', component: knowledgeIssuesPage }),
    createRoute({ ...parent, path: '/knowledge/versions/$id', component: knowledgeVersionPage }),
    createRoute({ ...parent, path: '/knowledge/$code', component: knowledgeDocumentPage }),
    createRoute({
      ...parent,
      path: '/partners/documents',
      component: partnerPendingDocumentsPage,
    }),
    createRoute({
      ...parent,
      path: '/partners/document-types',
      component: partnerDocumentTypesPage,
    }),
    createRoute({ ...parent, path: '/partners/portal-access', component: portalAccessPage }),
    createRoute({ ...parent, path: '/partners/$id', component: partnerDetailPage }),
  ]),
]);

const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
